import {Component, OnDestroy, OnInit} from '@angular/core';
import {HeaderService} from "../header.service";
import {ItemActionElement} from "../../../atoms/item-actions/item-action-element";
import {ItemActionService} from "../../../atoms/item-actions/item-action.service";
import {NotificationService} from "../../../atoms/notification/notification.service";
import {FavoritesService} from "../../../favorites/favorites.service";
import {combineLatest, Subscription} from "rxjs";
import {EntityType} from "../../../enums/entity-type";
import {Store} from "@ngrx/store";
import {
  SelfAssessmentIntroComponent
} from "../../../user/self-assessment/self-assessment-intro/self-assessment-intro.component";
import {OverlayService} from "../../../overlay/overlay.service";
import {NavigationStart, Router} from "@angular/router";
import {RemoveListDialogComponent} from "../../../favorites/remove-list-dialog/remove-list-dialog.component";
import {CdColor} from "../../../enums/cd-color";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {
  GlobalSearchOverlayComponent
} from "../../../search-filter/global-search-overlay/global-search-overlay.component";
import {DisplayService} from "../../../services/display.service";
import {getAllFavorites} from "../../../store/selectors/user.selector";
import {FavoriteEntityType} from "../../../enums/favorite-entity-type";
import {CodeInputService} from "../../../services/code-input.service";
import {StudentAuthService} from "../../../services/student-auth.service";
import {environment} from "../../../../environments/environment";
import {AuthService} from "../../../auth/auth.service";

@UntilDestroy()
@Component({
  selector: 'brt-mobile-header',
  templateUrl: './mobile-header.component.html',
  styleUrls: ['./mobile-header.component.scss']
})
export class MobileHeaderComponent implements OnInit, OnDestroy {
  searchText: string = "";

  menuItems = this.headerService.MENU_ITEMS;

  favoriteItemsNumber = 0;
  bookmarksActionMenu: ItemActionElement[] = [];
  favoriteItemsSubscription: Subscription | null = null;
  favoriteProfessionsNumber = 0;

  loginActionMenu: ItemActionElement[] = [];
  logoutActionMenu: ItemActionElement[] = [];

  isAnyLoggedIn = false;


  state = false;

  constructor(
    private store: Store,
    private overlay: OverlayService,
    private headerService: HeaderService,
    private itemAction: ItemActionService,
    private favorites: FavoritesService,
    private notificationService: NotificationService,
    private router: Router,
    private display: DisplayService,
    private codeInputService: CodeInputService,
    private studentAuthService: StudentAuthService,
    private authService: AuthService,
    ) {
  }

  ngOnInit() {
    this.activateFavoriteItemsNumberListener();
    this.generateBookmarksMenu();
    this.generateLoginMenuItems();
    this.generateLogoutMenuItems();


    this.headerService.onMobileMenuStateChanged().pipe(untilDestroyed(this)).subscribe((newVal) => {
      this.state = newVal;
      if(this.state) {
        this.display.disableBodyScroll();
      } else {
        this.display.enableBodyScroll();
      }
    })

    this.router.events.pipe(untilDestroyed(this)).subscribe((event) => {
      if(event instanceof NavigationStart) {
        this.headerService.setMobileMenuState(false);
      }
    })

    combineLatest([
      this.studentAuthService.onLoginStateChange(),
      this.authService.isAuthenticated$
    ]).pipe(untilDestroyed(this)).subscribe(([student, teacher]) => {
      //console.log(student, teacher);
      this.isAnyLoggedIn = student || teacher;

      this.generateLogoutMenuItems();
    })
  }

  activateFavoriteItemsNumberListener() {
    this.store.select(getAllFavorites()).pipe(untilDestroyed(this)).subscribe((favorites) => {
      this.favoriteItemsNumber = favorites.length
      this.favoriteProfessionsNumber = favorites.filter(el => el.type === FavoriteEntityType.PROFESSION).length;
      this.generateBookmarksMenu();
    });
  }

  generateBookmarksMenu() {
    const isEmpty = this.favoriteItemsNumber === 0;
    const singleItem = this.favoriteProfessionsNumber < 2;
    this.bookmarksActionMenu = [
      this.itemAction.generateItem("bookmark-full", "Merkliste anzeigen", true, () => { this.favorites.goToSavedProfessions(); }),
      this.itemAction.generateItem("compare", `${singleItem ? '' : this.favoriteProfessionsNumber} Berufe vergleichen`, !singleItem, () => {
        if(this.favoriteItemsNumber > 1) {
          this.router.navigateByUrl("/berufe-vergleichen");
        } else {
          this.notificationService.push("Du musst mind. 2 Berufe auswählen zum vergleichen", CdColor.RIVER, "");
        }
      }),
      this.itemAction.generateItem("code-note", "Merkliste speichern", !isEmpty, () => {
        this.favorites.save();
      }),
      this.itemAction.generateItem("code-enter", "Code eingeben", true, () => {
        this.favorites.openCodeWindow();
      }),
      this.itemAction.generateItem("trash", "Merkliste löschen", !isEmpty, () => {
        this.overlay.openWindowOverlay({title: "Merkliste löschen", componentMain: {component: RemoveListDialogComponent, data: {}}});
      })
    ];
  }

  toggleMenuItem(menuItem: HTMLDivElement) {
    menuItem.classList.toggle("opened");
  }

  openSelfAssessmentOverlay() {
    this.headerService.setMobileMenuState(false);
    this.overlay.addComponent({component: SelfAssessmentIntroComponent, data: {}});
  }

  ngOnDestroy() {
    this.favoriteItemsSubscription?.unsubscribe();
  }

  openGlobalSearch() {
    this.headerService.setMobileMenuState(false);

    this.overlay.addComponent({
      component: GlobalSearchOverlayComponent,
      data: {}
    })
  }

  generateLoginMenuItems(): void {

    if(environment.schoolClassPreviewMode) {
      this.loginActionMenu = [
        this.itemAction.generateItem("code-enter-klasse", "Klassenprofil öffnen", true, () => {
          this.codeInputService.openCodeInputService({
            title: "Gib hier das Klassenkennwort ein um in dein Klassenprofil einzusteigen:",
            pattern: "K***",
            submitButtonLabel: "Los",
            submitCallback: (code) => {
              this.studentAuthService.logIn(code).then((response) => {
                if(response) {
                  this.router.navigateByUrl('/klasse');
                } else {
                  this.notificationService.push("Login fehlgeschlagen", CdColor.RED, "x-thin");
                }
              });
            }
          })
        })
      ]
    } else {
      this.loginActionMenu = [
        this.itemAction.generateItem("code-enter-klasse", "Klassenprofil öffnen", true, async () => {
          this.headerService.setMobileMenuState(false);

          const tryLogin = await this.studentAuthService.logInUsingLocalStorage();
          if(tryLogin) {
            this.router.navigateByUrl('/klasse');
          } else {
            this.codeInputService.openCodeInputService({
              title: "Gib hier das Klassenkennwort ein um in dein Klassenprofil einzusteigen:",
              pattern: "K***",
              submitButtonLabel: "Los",
              submitCallback: (code) => {
                this.studentAuthService.logIn(code).then((response) => {
                  if(response) {
                    this.router.navigateByUrl('/klasse');
                  } else {
                    this.notificationService.push("Login fehlgeschlagen", CdColor.RED, "x-thin");
                  }
                });
              }
            })
          }
        }),
        this.itemAction.generateItem("login", "Bei Profil einloggen", true, () => {
          this.login();
        }),
        // this.itemAction.generateItem("login", "Als Anbieter anmelden", true, () => {
        //   window.open("https://www2.berufsreise.at/login", "_blank");
        // }),
        this.itemAction.generateItem("profile", "Kostenlos registrieren", true, () => {
          this.router.navigateByUrl('/registrierung');
        })
      ]
    }


  }

  generateLogoutMenuItems(): void {
    const menuItems: ItemActionElement[] = [];

    if(this.isAnyLoggedIn) {
      if(this.studentAuthService.isLoggedIn()) {
        menuItems.push(this.itemAction.generateItem("prize", "Zur Klasse", true, () => {
          this.router.navigateByUrl('/klasse');
        }))
      }

      if(this.authService.isAuthenticated()) {
        menuItems.push(this.itemAction.generateItem("prize", "Zum Profil", true, () => {
          this.router.navigateByUrl('/lehrer');
        }))
      }
    }

    menuItems.push(this.itemAction.generateItem("logout", "Abmelden", true, () => {
      this.logout();
    }))

    this.logoutActionMenu = menuItems;
  }

  login() {
    this.authService.login();
  }

  logout() {
    this.authService.logout();
  }

  loginSchoolClass() {
    this.codeInputService.openCodeInputService({
      title: "Gib hier das Klassenkennwort ein um in dein Klassenprofil einzusteigen:",
      pattern: "K***",
      submitButtonLabel: "Los",
      submitCallback: (code) => {
        this.studentAuthService.logIn(code).then((response) => {
          if(response) {
            this.router.navigateByUrl('/klasse');
          } else {
            this.notificationService.push("Login fehlgeschlagen", CdColor.RED, "x-thin");
          }
        });
      }
    })
  }

  openLoginMenu() {

  }

}
